import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import RightPanel from '../components/RightPanel';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const SpaceTimeFlowPage = () => {
  const [showPanel, setShowPanel] = useState(false);

  useEffect(() => {
    const handleDoubleClick = () => {
      setShowPanel(false);
    };

    document.addEventListener('dblclick', handleDoubleClick);
    return () => {
      document.removeEventListener('dblclick', handleDoubleClick);
    };
  }, []);

  const handleHeaderClick = () => {
    setShowPanel(true);
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="時空流系統 SpaceTime｜高階多維覺察與生命戰略升級遊戲"
        description="時空流系統 SpaceTime 為明悅文化打造的高維度遊戲化（Game-based）戰略推演平台，借古鑑今扮演大秦關鍵歷史角色，協助企業家與高階決策者升級高能動性 High Agency、賦能心智與生命格局成長。"
        path="/spacetime-flow"
        keywords="時空流, SpaceTime, 大秦往事, 遊戲化學習, Game-based Learning, 高能動性, High Agency, 賦能, 成長, 學習, 企業戰略, 高階主管, EMBA, 明悅文化, 明悅心創"
      />
      <Header onHeaderClick={handleHeaderClick} />
      <RightPanel isVisible={showPanel} />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-purple-100 via-indigo-50 to-blue-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-5xl lg:text-6xl font-serif text-gray-900 mb-6">
                時空流系統（SpaceTime System）
              </h1>
              <h2 className="text-3xl text-purple-600 font-semibold mb-8">
                高階多維覺察與生命戰略升級系統
              </h2>
              <p className="text-xl text-gray-600 italic">
                The Multidimensional Awareness & Strategy Upgrade Platform
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-xl mb-12">
              <p className="text-lg text-gray-700 leading-relaxed">
                作為財富流系統的<span className="font-bold text-purple-600">旗艦升級版</span>，時空流系統是一套引入高維度視角、著重於生命哲學與心智重塑的頂尖演練平台。系統超越了單純的物質財富計算，更深度探討表意識與潛意識的交織、物質與精神世界的平衡。透過更複雜的變數與多維度劇本，時空流系統協助高階領導者進行深度的生命覺察，突破既有的認知天花板，在時空推演中看見更宏大的生命藍圖，實現更高層次的富而喜悅。
              </p>
            </div>

            {/* Image 1 - Two side-by-side photos */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_instant-website-93/artifacts/uso77wal_%E6%99%82%E7%A9%BA%E6%B5%81%E5%A4%A7%E7%A7%A6.png"
                  alt="時空流大秦往事典藏禮盒"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_instant-website-93/artifacts/c01amuyr_%E6%99%82%E7%A9%BA%E6%B5%81.JPG"
                  alt="時空流沙盤桌面全景"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Positioning Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-serif text-gray-900 mb-4">產品定位與核心價值</h2>
              <p className="text-2xl text-purple-500 font-medium">Product Positioning & Core Values</p>
            </div>

            <div className="bg-gradient-to-r from-purple-500 to-indigo-600 rounded-2xl p-8 mb-12 text-center">
              <p className="text-2xl font-bold text-white">
                「借假修真、以史為鑑。跳出個體局限，開啟『上帝視角』的系統思維與生命戰略。」
              </p>
            </div>

            <div className="space-y-6 mb-12">
              <p className="text-lg text-gray-700 leading-relaxed">
                變局時代，管理者最難修練的是在黑天鵝頻傳的迷霧中，看清趨勢週期的「大局觀」。<span className="font-bold text-purple-600">《時空流：大秦往事》</span>是一套專為企業家、創辦人、高階決策者量身打造的「高維度心智架構與戰略決策演練場」。
              </p>

              <p className="text-lg text-gray-700 leading-relaxed">
                參與者將在沉浸式的推演中，換位扮演<span className="font-bold text-purple-600">秦始皇、呂不韋、荊軻、蒙恬</span>等九大歷史關鍵決策者。這是一場借古鑑今的「集體意識演練」，引導領導者跳出傳統商業的線性思維，站在歷史興衰的宏大週期之上，在無風險的沙盒中磨練戰略洞察力、高難度資源整合力與危機應變力。在瞬息萬變的情境中洞察人性與規律，全面提升解決複雜商業與生命難題的認知維度。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* System Mechanics Section */}
      <section className="py-16 bg-gradient-to-br from-purple-50 to-indigo-50">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-serif text-gray-900 mb-4">沙盤運作原理：高維意識轉化模型</h2>
              <p className="text-2xl text-purple-500 font-medium">System Mechanics</p>
            </div>

            <div className="bg-gradient-to-r from-purple-500 to-indigo-600 rounded-2xl p-8 mb-12 text-center">
              <p className="text-2xl font-bold text-white">
                「從『執念束縛』到『天啟解鎖』：一場戴著鐐銬跳舞的自我救贖與領導力蛻變。」
              </p>
            </div>

            {/* Image 2 */}
            <div className="bg-white rounded-2xl p-6 shadow-xl mb-12">
              <img 
                src="https://customer-assets.emergentagent.com/job_cultural-gateway-2/artifacts/0afblfy7_%E6%99%82%E7%A9%BA%E6%B5%81.png"
                alt="沙盤運作原理"
                className="w-full h-auto rounded-xl"
              />
            </div>

            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              本系統建立在<span className="font-bold text-purple-600">歷史高擬真模擬</span>與<span className="font-bold text-purple-600">多角色賽局（Multi-Agent Game Theory）</span>的複雜系統之上，獨創「角色—封印—天啟」的深度心智轉換閉環：
            </p>

            {/* Transformation Model */}
            <div className="bg-white rounded-2xl p-8 shadow-lg mb-12">
              <h3 className="text-2xl font-bold text-center text-purple-600 mb-8">
                【 時空流：心智維度躍遷模型 】
              </h3>
              
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-purple-100 to-indigo-100 rounded-xl p-6 border-l-4 border-purple-500">
                  <p className="text-lg font-semibold text-gray-800">
                    角色設定 (歷史領袖) ──► 承接宿命與時代巨變
                  </p>
                </div>
                
                <div className="text-center text-3xl font-bold text-purple-600">▼</div>
                
                <div className="bg-gradient-to-r from-purple-100 to-indigo-100 rounded-xl p-6 border-l-4 border-purple-500">
                  <p className="text-lg font-semibold text-gray-800">
                    封印枷鎖 (實體手環) ──► 投射現實卡點與執念
                  </p>
                </div>
                
                <div className="text-center text-3xl font-bold text-purple-600">▼</div>
                
                <div className="bg-gradient-to-r from-purple-100 to-indigo-100 rounded-xl p-6 border-l-4 border-purple-500">
                  <p className="text-lg font-semibold text-gray-800">
                    逆境覺察 (撕開封印) ──► 將限制性信念轉化為恩典
                  </p>
                </div>
                
                <div className="text-center text-3xl font-bold text-purple-600">▼</div>
                
                <div className="bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl p-6">
                  <p className="text-lg font-semibold text-white">
                    天啟解鎖 (終極願景) ──► 活出自由，邁向富而喜悅
                  </p>
                </div>
              </div>
            </div>

            {/* System Components */}
            <div className="space-y-8">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h4 className="text-xl font-bold text-purple-600 mb-4">
                  封印與執念的物化體驗
                </h4>
                <p className="text-lg text-gray-700 leading-relaxed">
                  每位歷史角色皆背負專屬的「封印卡」與物理手環，精準物化了現實生活中領導者難以擺脫的盲點、執念與限制性信念。這種深度的身體與心理體感，讓參與者切身體會自身在商場或人生中被「卡住」的真實狀態。
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h4 className="text-xl font-bold text-purple-600 mb-4">
                  恩典與天啟的轉化機制（The Transformation of Grace）
                </h4>
                <p className="text-lg text-gray-700 leading-relaxed">
                  推演過程中，領導者必須在特定高壓條件下，學會如何「撕開封印」，將生命中的負面束縛與逆境，轉化為背後的「恩典（Grace）」助力。唯有徹底完成心智突破、打破慣性盲點，並達成人生戰略目標者，方能解鎖終極的「天啟卡」。這是一場戴著鐐銬跳舞，最終親手打破鐐銬的生命預演，協助領導者將現實逆境，轉化為走向自由與豐盛的最高禮物。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Multidimensional Value Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-serif text-gray-900 mb-4">多維價值與社會意義</h2>
              <p className="text-2xl text-purple-500 font-medium">Multidimensional Value & CSR Impact</p>
            </div>

            <div className="space-y-12">
              {/* Corporate Strategy */}
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <span className="text-4xl">💡</span>
                  <h3 className="text-2xl font-bold text-gray-900">對企業發展與高管團隊的價值</h3>
                </div>
                <p className="text-lg text-purple-600 font-semibold mb-4">Corporate Strategy & Succession Planning</p>
                
                <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-gray-800 mb-3">
                    頂級企業高管的戰略預演沙盒 (Strategic Sandbox for Executive Teams)
                  </h4>
                  <p className="text-gray-700 leading-relaxed">
                    這是企業家與決策團隊進行戰略推演、凝聚集體共識、以及培養高階接班人的頂尖平台。透過模擬大秦帝國在不同擴張階段、資源重組時的巨大挑戰，協助管理團隊在沙盤上「模擬交學費」，看清團隊決策的盲點，避免在現實商業世界中付出慘痛代價。
                  </p>
                </div>
              </div>

              {/* Education Value */}
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <span className="text-4xl">💡</span>
                  <h3 className="text-2xl font-bold text-gray-900">對高階商學院與頂尖教育的價值</h3>
                </div>
                <p className="text-lg text-purple-600 font-semibold mb-4">Disruptive Case Teaching for EMBA/MBA</p>
                
                <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-gray-800 mb-3">
                    顛覆性的動態案例教學法 (Dynamic Experiential Learning)
                  </h4>
                  <p className="text-gray-700 leading-relaxed">
                    為全球 EMBA、MBA 及領袖總裁班課程，提供具備東方智慧與跨時代思維的顛覆性教學工具。它將靜態、過時的商業案例，轉化為動態、高頻、可親身參與的戰略博弈，讓學員在「身歷其境」中深化對戰略本質、權力結構與組織文化的理解。
                  </p>
                </div>
              </div>

              {/* Personal Growth */}
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <span className="text-4xl">💡</span>
                  <h3 className="text-2xl font-bold text-gray-900">對高階個人成長與格局提升的價值</h3>
                </div>
                <p className="text-lg text-purple-600 font-semibold mb-4">Cognitive Horizon Expansion</p>
                
                <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-gray-800 mb-3">
                    對接離火大運，躍遷生命格局 (Vibrational & Mental Upgrade)
                  </h4>
                  <p className="text-gray-700 leading-relaxed">
                    回應離火大運對於心靈、智慧與高能覺察的呼喚，協助參與者打破既有的認知天花板。透過體驗歷史的波瀾壯闊，領導者將學會敬畏週期、順應規律，不再受困於眼前的物質波動，而是以更宏大、更長遠的跨時空視角看待個人天命與事業發展。
                  </p>
                </div>
              </div>

              {/* Global Impact */}
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <span className="text-4xl">🌍</span>
                  <h3 className="text-2xl font-bold text-gray-900">國際接軌與社會價值</h3>
                </div>
                <p className="text-lg text-purple-600 font-semibold mb-4">Cultural Confidence & Global Leadership</p>
                
                <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-gray-800 mb-3">
                    傳承東方古典哲學，向世界輸出戰略智慧 (Globalizing Eastern Strategic Wisdom)
                  </h4>
                  <p className="text-gray-700 leading-relaxed">
                    立足台灣，接軌全球。明悅心創透過時空流系統，將中華優秀傳統文化中的大局觀、縱橫戰略與生命哲學，以極具現代感與科技感的遊戲化模型（Gamification Model）重新詮釋。這不僅能激發當代領袖的文化自信與歷史使命感，更能在全球意識躍遷的浪潮中，為世界培育具備全球視野（Global Vision）與東方智慧（Eastern Wisdom）的頂尖未來領袖。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SpaceTimeFlowPage;
